import { FC, useState, useRef, useEffect } from "react";
import { Group, ScrollArea, Space, Tabs, Title } from "@mantine/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  validateForm,
  FormErrors,
} from "@funded-here-interface/common/src/Utils/validateForm";
import ErrorSummary from "../../components/ErrorSummary";
import { useStyle } from "../../styles/BorrowerDocUpload";
import { useTabSync } from "./hooks/useTabSync";
import { useDirtyTracking } from "./hooks/useDirtyTracking";
import { useUnsavedChangesModal } from "./hooks/useUnsavedChangesModal";
import UnsavedChangesModal from "./components/UnsavedChangesModal";
import { TAB_MAPPING, TAB_ARRAY, getTabContent } from "./config/tabConfig";

const TAB_COUNT = 4;

/**
 * Merchant Onboarding Layout Component
 * Manages multi-step onboarding with dirty tracking and validation
 */
const MerchantLayout: FC = () => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state);

  // Tab synchronization with URL
  const { activeTab, setActiveTab } = useTabSync(TAB_MAPPING);

  // Dirty state tracking
  const {
    isDirty,
    handleDirtyChange,
    handleSaveDraftCallback,
    handleResetFormCallback,
    executeSaveDraft,
    executeResetForm,
  } = useDirtyTracking(TAB_COUNT);

  // Validation state
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const displayedErrors = hasAttemptedSubmit ? errors : {};

  // Unsaved changes modal
  const {
    showModal,
    modalAction,
    checkTabSwitch,
    handleSaveAndProceed,
    handleDiscardAndProceed,
    handleCancel,
  } = useUnsavedChangesModal({
    activeTab,
    isDirty,
    executeSaveDraft,
    executeResetForm,
  });

  // Panel refs for scrolling
  const panelRefs = useRef<(HTMLDivElement | null)[]>(
    Array(TAB_COUNT).fill(null)
  );

  // Reset state on mount
  useEffect(() => {
    dispatch({ type: "RESET_ONBOARDING_STATE" });
    setHasAttemptedSubmit(false);
  }, [dispatch]);

  // Initial validation
  useEffect(() => {
    const validationErrors = validateForm(state);
    setErrors(validationErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to active panel
  useEffect(() => {
    if (panelRefs.current[activeTab]) {
      panelRefs.current[activeTab]!.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeTab]);

  /**
   * Handle tab change with dirty check
   */
  const handleTabChange = (value: string | null) => {
    const newTab = Number(value);
    if (checkTabSwitch(newTab)) {
      setActiveTab(newTab);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    const validationErrors = validateForm(state);
    setErrors(validationErrors);
  };

  /**
   * Clear a specific error field
   */
  const handleClearError = (section: string, field: string) => {
    setErrors((prev) => {
      const sectionErrors = prev[section];
      if (sectionErrors && typeof sectionErrors === "object") {
        const { [field]: _, ...rest } = sectionErrors as Record<string, string>;
        return {
          ...prev,
          [section]: Object.keys(rest).length > 0 ? rest : undefined,
        };
      }
      return prev;
    });
  };

  /**
   * Handle modal save action
   */
  const onModalSave = () => {
    const newTab = handleSaveAndProceed();
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  /**
   * Handle modal discard action
   */
  const onModalDiscard = () => {
    const newTab = handleDiscardAndProceed();
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  /**
   * Get tabs with errors for visual indicators
   */
  const tabsWithErrors = Array.from(
    new Set(
      Object.keys(displayedErrors).map((section) => {
        const tab = TAB_ARRAY.find((t) => t.errorKey === section);
        return tab?.index;
      })
    )
  ).filter(Boolean);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Group position="apart">
        <Title>Onboarding</Title>
      </Group>
      <Space h="md" />

      {hasAttemptedSubmit && Object.keys(errors).length > 0 && (
        <ErrorSummary
          errors={errors}
          setActiveTab={(tab) => setActiveTab(Number(tab))}
        />
      )}

      <Tabs
        value={activeTab.toString()}
        onTabChange={handleTabChange}
        variant="outline"
        styles={(theme) => ({
          tabsList: {
            backgroundColor: theme.white,
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
            padding: theme.spacing.md,
            zIndex: 10,
          },
          tab: {
            padding: theme.spacing.md,
            fontWeight: 500,
            "&[data-active]": {
              borderBottomColor: theme.colors.blue[6],
            },
          },
          root: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
          },
        })}
      >
        <Tabs.List>
          {TAB_ARRAY.map((tab) => (
            <Tabs.Tab
              key={tab.index}
              value={tab.index}
              className={
                tabsWithErrors.includes(tab.index) ? classes.tabError : ""
              }
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <ScrollArea
          style={{
            flex: 1,
            maxHeight: "calc(100vh - 100px)",
            maxWidth: "150vh",
          }}
          type="auto"
          offsetScrollbars
        >
          {TAB_ARRAY.map((tab, index) => (
            <Tabs.Panel key={tab.index} value={tab.index} pt="md">
              <div ref={(el) => (panelRefs.current[index] = el)}>
                {getTabContent(index, {
                  onDirtyChange: (dirty: boolean) =>
                    handleDirtyChange(index, dirty),
                  onSaveDraft: (callback: () => void) =>
                    handleSaveDraftCallback(index, callback),
                  onResetForm: (callback: () => void) =>
                    handleResetFormCallback(index, callback),
                  errors: displayedErrors,
                  onClearError: handleClearError,
                  onSubmit: index === 3 ? handleSubmit : undefined,
                })}
              </div>
            </Tabs.Panel>
          ))}
        </ScrollArea>
      </Tabs>

      <UnsavedChangesModal
        opened={showModal}
        action={modalAction}
        onSave={onModalSave}
        onDiscard={onModalDiscard}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default MerchantLayout;
